/**
 * 身份证识别结论 → 界面呈现（标签 + 需要告知的问题）
 * -----------------------------------------------------------------------------
 * 为什么要有这一层：`KycOcrAnalysis` 是**给机器判定用的**字段集合
 * （`avatarDetected` / `risk.concluded` / `numberConsistency.code` 都是这套判定的中间量），
 * 直接往界面上贴会出现两类错：
 *
 *   ① **把缺失当正常** —— `risk.type` 默认值是 `'unknown'`，`risk.concluded` 才是
 *      「供应商到底给没给结论」。只读 `type !== 'edited'` 就贴「原件拍摄」，
 *      等于在供应商没做检测时也向学员保证「这是原件」。
 *   ② **把降级当没事** —— 头像没切出来时链路照样能跑（比对退回整卡图），
 *      但那会让 ④ 的分数明显偏低、学员白被扣一次分。这种情况必须当场说出来。
 *
 * 所以这里只做两件事：列出**已确认通过**的标签，列出**会影响后续步骤**的提示。
 * 服务端已经拦截的失败不在此列（失败时根本到不了这里，走的是 Toast 报错）。
 */
import type { KycOcrAnalysis } from '../../api/types';

export interface OcrPresentation {
  /** 已确认通过的项，正面表述 */
  chips: string[];
  /** 不阻断但必须让学员知道的事 */
  warnings: string[];
}

export function describeOcrAnalysis(analysis: KycOcrAnalysis): OcrPresentation {
  const chips: string[] = [];

  // 质量三项：只在供应商真的给了结论、且该项为 true 时才贴。
  // `quality === null` 表示没开检测，此时一个标签都不贴（而不是贴三个灰的）
  if (analysis.quality) {
    if (analysis.quality.isClear) chips.push('清晰');
    if (analysis.quality.isComplete) chips.push('边框完整');
    if (analysis.quality.isNoCover) chips.push('无遮挡');
  }

  // 原件结论必须同时满足「供应商给了结论」+「结论是 normal」+「未判定被 PS」
  if (analysis.risk.concluded && analysis.risk.type === 'normal' && !analysis.risk.edited) {
    chips.push('原件拍摄');
  }

  // 方向被自动纠正过才提 —— 没转过还标「已矫正」会让学员以为拍歪了还能用
  if (analysis.correction.rotated) chips.push('已自动矫正方向');

  const warnings: string[] = [];

  if (!analysis.avatarDetected) {
    warnings.push('未能从证件中提取头像，后续人脸比对将改用整张证件图，分数可能偏低');
  }

  if (analysis.sideReversed) {
    warnings.push('检测到正反面放反，已自动纠正；下次请把国徽面放到国徽面位置');
  }

  if (!analysis.numberConsistency.consistent) {
    // 不拦截（`06-身份核验.md` §2.1：只记录），但学员必须核对 ——
    // 这类不一致往往就是号码抄错了一位，等到 ② 才报错已经多花一次重试
    warnings.push(
      `证件号与证件上印刷的性别/出生信息不一致${analysis.numberConsistency.detail ? `（${analysis.numberConsistency.detail}）` : ''}，请核对号码`,
    );
  }

  return { chips, warnings };
}
